<template>
  <teleport to="#modal">
    <FadeTransition v-if="noBackdrop">
      <div v-show="shown" :class="$style['modal--noBackdrop']">
        <slot />
      </div>
    </FadeTransition>
    <FadeTransition v-else>
      <div
        :class="$style.modal"
        v-bind="$attrs"
        v-show="shown"
        @click.self="onClickBackdrop"
      >
        <SlideTransition>
          <div
            v-if="shown"
            :class="[
              $style.modal__outer,
              {
                [$style['modal__outer--unscrollable']]: noScroll,
              },
            ]"
            @click.self="onClickBackdrop"
          >
            <div
              :class="[
                $style.modal__inner,
                {
                  [$style['modal__inner--unscrollable']]: noScroll,
                  [$style['modal__inner--closeable']]: closeable,
                },
              ]"
            >
              <slot />
              <IconCloseCircle
                v-if="closeable"
                :class="$style.modal__close"
                @click="$emit('close')"
              />
            </div>
          </div>
        </SlideTransition>
      </div>
    </FadeTransition>
  </teleport>
</template>

<script lang="ts">
export default {
  inheritAttrs: false,
}
</script>

<script setup lang="ts">
import { watch } from 'vue'
import FadeTransition from '@/components/transitions/FadeTransition.vue'
import SlideTransition from '@/components/transitions/SlideTransition.vue'
import IconCloseCircle from '@/components/icons/IconCloseCircle.vue'

const props = withDefaults(
  defineProps<{
    shown?: boolean
    disableBackdrop?: boolean
    noBackdrop?: boolean
    noScroll?: boolean
    closeable?: boolean
  }>(),
  {
    shown: false,
    disableBackdrop: false,
    noBackdrop: false,
    noScroll: false,
    closeable: false,
  }
)

const emit = defineEmits<{
  (event: 'opened'): void
  (event: 'close'): void
  (event: 'closed'): void
  (event: 'clickBackdrop'): void
}>()

const onClickBackdrop = () => {
  emit('clickBackdrop')

  if (props.disableBackdrop) {
    return
  }
  emit('close')
}

watch(
  () => props.shown,
  (value, oldValue) => {
    if (value && !oldValue) {
      emit('opened')
    }
    if (!value && oldValue) {
      emit('closed')
    }
  }
)
</script>

<style lang="scss" module>
.modal {
  $gutter: 36px;

  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: $color-surface-backdrop;

  &__outer {
    overflow: scroll;
    max-width: 100%;
    max-height: 100vh;

    &::-webkit-scrollbar {
      display: none;
    }

    &--unscrollable {
      overflow: hidden;
    }
  }

  &__inner {
    position: relative;
    max-width: calc(100vw - $gutter * 2);
    margin: $gutter;
    background-color: $color-surface-primary;
    border-radius: $border-radius-rounder;
    box-shadow: $shadow-32;

    &--unscrollable {
      height: calc(100vh - $gutter * 2);
    }

    &--closeable {
      margin-bottom: 50px;
    }
  }

  &--noBackdrop {
    position: absolute;
    margin: auto;
  }

  &__close {
    font-size: 40px;
    position: absolute;
    color: $color-text-and-object-high-emphasis-inverse;
    left: 50%;
    bottom: -50px;
    transform: translateX(-50%);
    cursor: pointer;
  }
}
</style>
